'use client';

/**
 * Session state for the CMS.
 *
 *   CMS → FastAPI Admin API (/api/v1/admin/auth/*) → Supabase Auth
 *
 * Credentials are exchanged by the Admin API rather than by a browser Supabase
 * client. The backend owns that exchange already (`POST /auth/login` returns the
 * Supabase session), and routing through it means the browser needs no Supabase
 * key of its own and the token is guaranteed to be one the API will accept.
 *
 * The token is persisted by `@/lib/auth/token`; `src/middleware.ts` reads the
 * same cookie to gate routes before a page renders.
 */

import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authApi } from '@/lib/api/endpoints';
import { ApiError } from '@/lib/api/client';
import type { AuthenticatedUser } from '@/lib/api/types';
import { clearSession, getStoredUser, getToken, saveSession } from '@/lib/auth/token';

/**
 * Shape kept compatible with the Supabase user object the UI was written
 * against (`user.email`, `user.user_metadata.full_name`), so components that
 * read those fields keep working unchanged.
 */
export interface SessionUser {
  id: string;
  email: string | null;
  user_metadata: Record<string, unknown>;
  role: string | null;
  profile: AuthenticatedUser['profile'];
  is_active: boolean | null;
}

interface AuthContextValue {
  user: SessionUser | null;
  loading: boolean;
  /** Set when the session could not be confirmed against the Admin API. */
  error: string | null;
  signIn: (email: string, password: string) => Promise<SessionUser>;
  signUp: (email: string, password: string, fullName?: string) => Promise<void>;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
  getJWT: () => string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};

function toSessionUser(user: AuthenticatedUser): SessionUser {
  return {
    id: user.id,
    email: user.email,
    user_metadata: { ...user.user_metadata, full_name: user.full_name },
    role: user.role,
    profile: user.profile,
    is_active: user.is_active,
  };
}

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  /** Confirm the stored token with GET /auth/me and hydrate the user from it. */
  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      setLoading(false);
      return;
    }

    // Show the cached identity immediately so a reload does not flash an empty
    // topbar while /auth/me is in flight.
    const cached = getStoredUser();
    if (cached) {
      setUser({
        id: cached.id,
        email: cached.email,
        user_metadata: { full_name: cached.full_name },
        role: null,
        profile: null,
        is_active: null,
      });
    }

    try {
      const me = await authApi.me();
      setUser(toSessionUser(me));
      setError(null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // Expired or revoked — the client already cleared the token.
        clearSession();
        setUser(null);
        setError(null);
      } else {
        // The API is unreachable. Keep the cached identity rather than bouncing
        // the operator to /login over a backend that is merely down.
        setError(err instanceof Error ? err.message : 'Could not verify the session.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signIn = useCallback(async (email: string, password: string) => {
    const session = await authApi.login(email, password);
    // The refresh token is stored alongside the access token. It is what lets
    // `SessionWatcher` renew the session before its hour is up, rather than the
    // operator being signed out mid-pilot; see `lib/auth/refresh.ts`.
    saveSession(
      session.access_token,
      session.expires_in,
      {
        id: session.user.id,
        email: session.user.email,
        full_name: session.user.full_name,
      },
      session.refresh_token
    );

    const sessionUser: SessionUser = {
      id: session.user.id,
      email: session.user.email,
      user_metadata: { ...session.user.user_metadata, full_name: session.user.full_name },
      role: session.user.role,
      profile: null,
      is_active: null,
    };
    setUser(sessionUser);
    setError(null);

    // Pull the CMS-side profile in the background; login must not fail because
    // the profile lookup did.
    void authApi
      .me()
      .then((me) => setUser(toSessionUser(me)))
      .catch(() => undefined);

    return sessionUser;
  }, []);

  const signUp = useCallback(async (email: string, password: string, fullName?: string) => {
    await authApi.register(email, password, fullName);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Logout is idempotent backend-side; a failure here must still end the
      // local session.
    }
    clearSession();
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, error, signIn, signUp, signOut, refresh, getJWT: getToken }),
    [user, loading, error, signIn, signUp, signOut, refresh]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
